import React, { useEffect, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../common/common.style";
import { FaTrash } from "react-icons/fa";
import { ModalDeleteAll } from "./ModalDeleteAll";
import { CardFrameType, CardSelected } from "../../../type";
import { LoadingOverlay } from "../../common/LoadingOverley/LoadingOverley";
import { useSelector } from "react-redux";
import { selectGame, selectToken } from "../../../store/slices/global";
import { Box, FlexBox } from "../../primitives";
import { useCards } from "../../../context/CardsContext";
import { useBatchCreateCardTraderToUploadMutation } from "../../../services/myServer/cardToUpload";
import { CardToUploadBatchCreateRequestType } from "magic-shared-types";
import { ImportExportCsv } from "./ImportExportCsv";
import { IconNameText, WrapperIcon } from "./header.styled";
import localforage from "localforage";
import { ModalUploadHandler } from "./ModalUploadHandler";
import { ModalChooseStrategy } from "../../ModalChooseStrategy/ModalChooseStrategy";
import { SelectDeselectCards } from "./SelectDeselectCards";
import { v4 as uuidv4 } from "uuid";
import { useLazyGetAllCardFromMachineQuery } from "../../../services/myServer/uploadingCardFromMachine";
import dayjs from "dayjs";

interface HeaderProps {
  isCardsBoardEmpty: boolean;
  setModalAddPurchasePrice: (arg1: boolean) => void;
  setCardsBoard: React.Dispatch<React.SetStateAction<CardSelected[]>>;
  cardsBoard: CardSelected[];
  cardsBoardCanDoOpeation: CardSelected[];
  cardsBoardFiltered: CardSelected[];
  cardsClicked: string[];
  setCardsClicked: (arg1: string[]) => void;
}

export const Header = ({
  isCardsBoardEmpty,
  setModalAddPurchasePrice,
  setCardsBoard,
  cardsBoard,
  cardsBoardCanDoOpeation,
  cardsBoardFiltered,
  cardsClicked,
  setCardsClicked,
}: HeaderProps) => {
  const cardComplexArray = useCards();
  const game = useSelector(selectGame);
  const token = useSelector(selectToken);
  const [isDeleteAllOpen, setIsDeleteAll] = useState(false);
  const [isModalUploadOpen, setIsModalUploadOpen] = useState(false);
  const [isApiUploadLoading, setIsApiUploadLoading] = useState(false);
  const [openModalPriceCards, setOpenModalPriceCards] = useState(false);
  const [trigger, { data }] = useLazyGetAllCardFromMachineQuery();

  useEffect(() => {
    if (data) {
      setCardsBoard((prev) => [
        ...prev,
        ...data.map((card) => {
          const cardFound = cardComplexArray[card.blue_print_id];
          return {
            uid: uuidv4(),
            id: card.blue_print_id,
            name: cardFound.name,
            game_id: 1,
            quantity: 1,
            price: 1000,
            condition: card.condition,
            language: card.language,
            finish: "regular" as CardFrameType,
            signed: false,
            altered: false,
            description: "",
            isReversed: false,
            isFirstEdition: false,
            date: dayjs().toISOString(),
          };
        }),
      ]);
    }
  }, [cardComplexArray, data, setCardsBoard]);

  const [batchCreateCardTraderToUpload] =
    useBatchCreateCardTraderToUploadMutation();

  function mergeCards(
    cards: CardSelected[]
  ): CardToUploadBatchCreateRequestType[] {
    const merged = new Map<string, CardToUploadBatchCreateRequestType>();

    for (const card of cards) {
      const key = JSON.stringify({
        blue_print_id: card.id,
        condition: card?.condition,
        is_foil: card.finish === "foil" || false,
        description: card?.description || "",
        language: card.language,
        is_signed: card?.signed || false,
        is_first_edition: card?.isFirstEdition || false,
        is_reverse: card?.isReversed || false,
        is_altered: false,
      });

      const existing = merged.get(key);

      const isFoil = card.finish === "foil";
      const foundCardComplexArray = cardComplexArray[card.id];
      const cardMarketId =
        foundCardComplexArray?.card_market_ids?.length === 2 && isFoil
          ? foundCardComplexArray?.card_market_ids?.[1]
          : foundCardComplexArray?.card_market_ids?.[0];

      const newEntry: CardToUploadBatchCreateRequestType = {
        blue_print_id: card.id,
        card_name: card.name,
        price: card.price < 0.02 ? 0.02 : card.price,
        condition: card?.condition,
        is_foil: card.finish === "foil" || false,
        description: card?.description || "",
        language: card.language,
        card_market_id: cardMarketId,
        quantity: card.quantity,
        is_signed: card?.signed || false,
        is_first_edition: card?.isFirstEdition || false,
        is_reverse: card?.isReversed || false,
        is_altered: false,
        game_id: game.id,
      };

      if (existing) {
        existing.quantity += card.quantity; // ✅ Merge quantities
      } else {
        merged.set(key, newEntry);
      }
    }

    return Array.from(merged.values());
  }

  const onClickUploadHandler = async () => {
    setIsApiUploadLoading(true);
    const BATCH_SIZE = 250;

    const splitIntoBatches = (
      array: CardToUploadBatchCreateRequestType[],
      size: number
    ): CardToUploadBatchCreateRequestType[][] => {
      const batches = [];
      for (let i = 0; i < array.length; i += size) {
        batches.push(array.slice(i, i + size));
      }
      return batches;
    };

    const mergedCards = mergeCards(cardsBoardCanDoOpeation);

    const batches = splitIntoBatches(mergedCards, BATCH_SIZE);
    let tempBoard = [...cardsBoard];

    for (let i = 0; i < batches.length; i++) {
      const currentBatch = batches[i];

      try {
        await batchCreateCardTraderToUpload({ cards: currentBatch }).unwrap();

        // Remove successfully uploaded cards by their blue_print_id
        const uploadedIds = new Set(currentBatch.map((c) => c.blue_print_id));
        tempBoard = tempBoard.filter((card) => !uploadedIds.has(card.id));

        console.log(`✅ Batch ${i + 1}/${batches.length} uploaded`);
      } catch (err) {
        console.error(`❌ Batch ${i + 1} failed`, err);

        // Optionally flag cards with error
        const failedIds = new Set(currentBatch.map((c) => c.blue_print_id));
        tempBoard = tempBoard.map((card) =>
          failedIds.has(card.id)
            ? {
                ...card,
                hasPriceIssue: true,
                uploadingError: "CardTrader uploading error",
              }
            : card
        );
      }
    }

    setCardsBoard(tempBoard);
    if (!tempBoard.length) {
      await localforage.removeItem("cardsBoard");
    }
    setIsApiUploadLoading(false);
  };

  return (
    <>
      {openModalPriceCards && (
        <ModalChooseStrategy
          setCardsBoard={setCardsBoard}
          cardsBoard={cardsBoard}
          cardsBoardCanDoOpeation={cardsBoardCanDoOpeation}
          setOpen={setOpenModalPriceCards}
        />
      )}
      {isApiUploadLoading && <LoadingOverlay />}
      {isDeleteAllOpen && (
        <ModalDeleteAll
          setIsDeleteAll={setIsDeleteAll}
          setCardsBoard={setCardsBoard}
        />
      )}
      {isModalUploadOpen && (
        <ModalUploadHandler
          onClickUploadHandler={onClickUploadHandler}
          setIsModalUploadOpen={setIsModalUploadOpen}
        />
      )}
      <FlexBox
        style={{
          width: "100%",
          borderBottom: "1px solid #cbd5e1",
          alignItems: "center",
          padding: 20,
          flexWrap: "wrap",
          gap: 20,
        }}
      >
        <FlexBox css={{ gap: 20, alignItems: "center" }}>
          <WrapperIcon>
            <Box
              style={{ cursor: "pointer" }}
              onClick={() => setIsDeleteAll(true)}
            >
              <FaTrash size={20} color="red" />
            </Box>
            <IconNameText>Delete all</IconNameText>
          </WrapperIcon>
          <ImportExportCsv
            cardsBoard={cardsBoard.filter((card) => card.game_id === game.id)}
            setCardsBoard={setCardsBoard}
            cardsClicked={cardsClicked}
          />
          <SelectDeselectCards
            cardsBoardFiltered={cardsBoardFiltered}
            setCardsClicked={setCardsClicked}
            cardsClicked={cardsClicked}
          />
          <SecondaryButton
            onClick={() => {
              trigger();
            }}
          >
            PULL CARD
          </SecondaryButton>
        </FlexBox>

        <FlexBox style={{ marginLeft: "auto", gap: 10 }}>
          <PrimaryButton
            isDisabled={isCardsBoardEmpty || !token}
            disabled={isCardsBoardEmpty || !token}
            onClick={() => setModalAddPurchasePrice(true)}
            css={{ "@mdRev": { width: "30%" } }}
          >
            ADD PURCHASE COMMENTS
          </PrimaryButton>
          <PrimaryButton
            disabled={isCardsBoardEmpty}
            isDisabled={isCardsBoardEmpty}
            onClick={() => setOpenModalPriceCards(true)}
            css={{ "@mdRev": { width: "30%" } }}
          >
            PRICE CARDS
          </PrimaryButton>
          <PrimaryButton
            disabled={isCardsBoardEmpty || !token}
            isDisabled={isCardsBoardEmpty || !token}
            onClick={() => setIsModalUploadOpen(true)}
            css={{ "@mdRev": { width: "30%" } }}
          >
            UPLOAD
          </PrimaryButton>
        </FlexBox>
      </FlexBox>
    </>
  );
};
